import { useState, useRef } from "react";
import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Button, Input, InputNumber, Select, Modal } from "antd";
import { CalendarOutlined, CloseOutlined, SettingOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const COLOR = "#7c3aed";

const PROVIDERS = [
  { value: "google", label: "Google Meet" },
  { value: "zoom",   label: "Zoom" },
];

const TIMEZONES = [
  { value: "Asia/Dubai",      label: "UAE (GST +4)" },
  { value: "Asia/Karachi",    label: "Pakistan (PKT +5)" },
  { value: "Asia/Kolkata",    label: "India (IST +5:30)" },
  { value: "America/New_York",label: "Eastern (ET)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "Europe/London",   label: "London (GMT/BST)" },
  { value: "Europe/Paris",    label: "Central Europe (CET)" },
  { value: "UTC",             label: "UTC" },
];

const ScheduleMeetingNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const dataRef = useRef(data);
  dataRef.current = data;

  const [open, setOpen] = useState(false);
  const [provider,    setProvider]    = useState(data.provider    || "google");
  const [title,       setTitle]       = useState(data.title       || "Voycell Demo — {{firstname}}");
  const [description, setDescription] = useState(data.description || "Demo meeting requested via WhatsApp.");
  const [dateVar,     setDateVar]     = useState(data.dateVar     || "demo_date");
  const [duration,    setDuration]    = useState(data.duration    || 60);
  const [timezone,    setTimezone]    = useState(data.timezone    || "Asia/Dubai");
  const [meetLinkVar, setMeetLinkVar] = useState(data.meetLinkVar || "meetLink");
  const [meetTimeVar, setMeetTimeVar] = useState(data.meetTimeVar || "meetTime");

  const openModal = () => {
    setProvider(dataRef.current.provider    || "google");
    setTitle(dataRef.current.title          || "Voycell Demo — {{firstname}}");
    setDescription(dataRef.current.description || "Demo meeting requested via WhatsApp.");
    setDateVar(dataRef.current.dateVar      || "demo_date");
    setDuration(dataRef.current.duration    || 60);
    setTimezone(dataRef.current.timezone    || "Asia/Dubai");
    setMeetLinkVar(dataRef.current.meetLinkVar || "meetLink");
    setMeetTimeVar(dataRef.current.meetTimeVar || "meetTime");
    setOpen(true);
  };

  const handleSave = () => {
    dataRef.current.onChange?.({
      ...dataRef.current,
      provider, title, description, dateVar, duration, timezone, meetLinkVar, meetTimeVar,
    });
    setOpen(false);
  };

  const providerLabel = PROVIDERS.find((p) => p.value === (data.provider || "google"))?.label || "Google Meet";
  const configured = !!(data.dateVar && data.title);

  return (
    <>
      <div style={{
        background: "#fff",
        border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
        borderRadius: 10,
        padding: 14,
        minWidth: 260,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}>
        <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={COLOR} />
        <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <CalendarOutlined style={{ color: COLOR, fontSize: 16 }} />
          <strong style={{ fontSize: 13 }}>Schedule Meeting</strong>
          <Button
            type="text" size="small" icon={<CopyOutlined />}
            onClick={() => data.onDuplicate?.()}
            style={{ marginLeft: "auto", color: "#9ca3af" }}
          />
          <Button
            type="text" size="small" icon={<CloseOutlined />}
            onClick={() => deleteElements({ nodes: [{ id }] })}
            style={{ color: "#9ca3af" }}
          />
        </div>

        <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <NodeNameInput data={data} />
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
            Provider: <strong>{providerLabel}</strong>
          </div>
          {data.dateVar && (
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
              Date from: <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>{`{{${data.dateVar}}}`}</code>
            </div>
          )}
          {data.meetLinkVar && (
            <div style={{ fontSize: 11, color: "#059669", marginBottom: 6 }}>
              Outputs: <code style={{ background: "#f0fdf4", padding: "1px 4px", borderRadius: 3 }}>{`{{${data.meetLinkVar}}}`}</code>
            </div>
          )}
          {!configured && (
            <div style={{ fontSize: 11, color: "#f59e0b", marginBottom: 6 }}>Not configured</div>
          )}
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={openModal}
            style={{ width: "100%", fontSize: 12 }}
          >
            Configure Meeting
          </Button>
        </div>

        <Handle type="source" position={Position.Right} style={{ background: COLOR }} />
      </div>

      <Modal
        title="Configure Schedule Meeting"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ disabled: !dateVar || !title }}
        width={560}
        destroyOnClose={false}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8 }}>
          <div>
            <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Meeting Provider</label>
            <Select value={provider} onChange={setProvider} style={{ width: "100%" }} options={PROVIDERS} />
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>
              Date Variable <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>(session variable containing the user's typed date)</span>
            </label>
            <Input
              value={dateVar}
              onChange={(e) => setDateVar(e.target.value.replace(/\s/g, "_"))}
              placeholder="demo_date"
              addonBefore="{{"
              addonAfter="}}"
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
              Must match the "Save as variable" field on the CaptureInput node that captures the date
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Meeting Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Voycell Demo — {{firstname}}"
            />
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Description</label>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Demo meeting requested via WhatsApp."
            />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Duration (minutes)</label>
              <InputNumber value={duration} onChange={setDuration} min={15} max={480} style={{ width: "100%" }} />
            </div>
            <div style={{ flex: 2 }}>
              <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Timezone (for date parsing)</label>
              <Select value={timezone} onChange={setTimezone} style={{ width: "100%" }} options={TIMEZONES} />
            </div>
          </div>

          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 8 }}>Output Variables</div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Meet link variable</label>
                <Input
                  value={meetLinkVar}
                  onChange={(e) => setMeetLinkVar(e.target.value.replace(/\s/g, "_"))}
                  size="small"
                  addonBefore="{{"
                  addonAfter="}}"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Formatted time variable</label>
                <Input
                  value={meetTimeVar}
                  onChange={(e) => setMeetTimeVar(e.target.value.replace(/\s/g, "_"))}
                  size="small"
                  addonBefore="{{"
                  addonAfter="}}"
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
              Use <code>{`{{${meetLinkVar}}}`}</code> and <code>{`{{${meetTimeVar}}}`}</code> in later text / email nodes
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ScheduleMeetingNode;
